import {
  ParsedReportParams,
  ReportBackendTypes,
  useGetShareGroups,
} from '@ais-ui/websockets';
import { v4 as uuidv4 } from 'uuid';
import { ReportType } from '../../../reportsTypes';
import { useCallback, useMemo } from 'react';
import { getFrontendColorScaleFromBackend } from '../../utils/densityColorMapping';
import { parseCustomDuration } from '../../../../utils/formatDuration';
import {
  parseCsvMiscellaneousDataFields,
  parseCsvMiscellaneousMessageTypes,
} from '../../utils/parseCsvMiscellaneous';
import { convertToMinCrossingInterval } from '../../utils/convertToMinCrossingInterval';
import { parseCoverageMiscellaneousPlotType } from '../../utils/parseCoverageMiscellaneous';
import { convertBackToColorIntervals } from '../../utils/parseColorInterval';
import {
  useConditionalEventdetectorDefinitionsFetch,
  useConditionalWatchdogDefinitionsFetch,
} from '@ais-ui/api';
import { SelectType } from '@ais-ui/ui-core';
import { safeJsonParse } from '../../utils/safeJsonParse';

export function useInitializeMiscellaneous(
  report: ParsedReportParams | undefined,
  reportType: ReportType,
) {
  const { data: groups, isLoading } = useGetShareGroups();
  const {
    data: watchdogDefinitions,
    isLoading: isLoadingWatchdogs,
    isError: isErrorWatchdogs,
  } = useConditionalWatchdogDefinitionsFetch(
    reportType.type === ReportBackendTypes.WATCHDOG_EVENTS,
  );

  const {
    data: eventDefinitions,
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
  } = useConditionalEventdetectorDefinitionsFetch(
    reportType.type === ReportBackendTypes.WATCHDOG_EVENTS,
  );

  const matchingWatchdogAndEventDefinitions = useMemo(() => {
    if (!watchdogDefinitions?.records || !eventDefinitions?.records) {
      return [];
    }

    const watchdogAndEventIds = report?.params['WD'] || [];

    const watchdogMapped = watchdogDefinitions.records
      .filter((record) => watchdogAndEventIds.includes(record.id))
      .map((record) => ({
        label: record.name,
        value: record.id,
        type: 'watchdog', // Optional to distinguish source
      }));

    const eventMapped = eventDefinitions.records
      .filter((record) => watchdogAndEventIds.includes(record.id))
      .map((record) => ({
        label: record.name,
        value: record.id,
        type: 'event', // Optional to distinguish source
      }));

    return [...watchdogMapped, ...eventMapped];
  }, [watchdogDefinitions, eventDefinitions, report]);

  const initializeMiscellaneous = useCallback(
    (miscellaneousFields: Record<string, any>) => {
      const notifyDefaults = {
        // Default notification values for new reports - should be empty to avoid validation conflicts
        email: [],
        countryCode: '', // ❌ Remove the incorrect +45 default
        phone: '',
      };

      // If no report exists (creating new), provide defaults for all report types
      if (!report) {
        const shareDefaults = {
          selectedGroups: [],
        };

        switch (reportType.type) {
          case ReportBackendTypes.AREA:
            return {
              statisticGrouping: 'ungrouped', // Default value
              interval: 'day', // Default interval
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.DENSITY:
            return {
              colorScale: 'yellow to red', // Default color scale
              colorBy: 'unique_ships', // Default color by
              cellsPerDegree: '10', // Default cells per degree
              colorDivision: 10, // Default color division
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.TRIPS:
            return {
              maxDuration: { days: 1, hours: 0, minutes: 0 }, // Default max duration
              excludeLocalOperations: false, // Default exclude local ops
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP:
            return {
              maxClosestPointOfApproachDistance: 500, // Default CPA distance
              minDeltaCourseOverGround: 0, // Default values
              maxDeltaCourseOverGround: 180,
              minDeltaSpeedOverGround: 0,
              maxDeltaSpeedOverGround: 30,
              minSpeedOverGround: 0,
              maxSpeedOverGround: 30,
              minDuration: 300,
              maxDuration: 3600,
              minTravelDistance: 0,
              maxTravelDistance: 10000,
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT:
            return {
              distanceThreshold: 500, // Default distance threshold
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.PASSAGELINE:
            return {
              outputType: 'traffic', // Default output type
              statisticGrouping: 'ungrouped', // Default grouping
              interval: 'day', // Default interval
              crossingConfig: false, // Default crossing config
              minCrossingInterval: { amount: 30, unit: 'minute' }, // Default min crossing interval
              minCrossingDistance: 0, // Default min crossing distance
              columnDivisionValue: undefined, // Default column division
              columnDivisionUnit: undefined,
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.PASSAGELINE_SHIPS:
            return {
              maxDuration: { days: 1, hours: 0, minutes: 0 }, // Default max duration
              outputType: 'traffic', // Default output type
              statisticGrouping: 'ungrouped', // Default grouping
              interval: 'day', // Default interval
              bothDirections: false, // Default both directions
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.TRANSPONDER_OFF:
            return {
              maxDuration: { days: 0, hours: 1, minutes: 0 }, // Default max duration
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.SOG_DISTRIBUTION:
            return {
              distribution: 'ungrouped', // Default distribution
              interval: 'day', // Default interval
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.FISHING:
            return {
              minDuration: { days: 0, hours: 0, minutes: 30 }, // Default min duration
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.MULTI_SHAPE:
            return {
              maxDuration: { days: 1, hours: 0, minutes: 0 }, // Default max duration
              outputType: 'traffic', // Default output type
              statisticGrouping: 'ungrouped', // Default grouping
              interval: 'day', // Default interval
              bothDirections: false, // Default both directions
              ...notifyDefaults,
              ...shareDefaults,
            };
          case ReportBackendTypes.REPLAY_VIDEO:
            return {
              videoTitle: '',
              videoSpeed: '1',
              videoResolution: '640x480',
              videoFormat: 'mp4',
              videoQuality: 'low',
              mapType: 'osm',
              videoSegments: [],
              showTracks: false,
              ...notifyDefaults,
              ...shareDefaults,
            };
          default:
            return {
              ...notifyDefaults,
              ...shareDefaults,
            };
        }
      }

      const shareDefaults = {
        selectedGroups:
          groups && Array.isArray(report?.params?.['SR'])
            ? groups
                .filter((group) => report.params['SR'].includes(group.uuid))
                .map((group) => ({
                  value: group.uuid,
                  label: group.name,
                }))
            : [],
      };

      switch (reportType.type) {
        case ReportBackendTypes.AREA:
          return {
            statisticGrouping: report?.params['STG'],
            interval: report?.params['PI'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.DENSITY:
          return {
            colorScale: getFrontendColorScaleFromBackend(report?.params['CS']),
            colorBy: report?.params['CB'],
            cellsPerDegree: report?.params['CPD']
              ? report?.params['CPD'].toString()
              : undefined,
            colorDivision: report?.params['CD'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.TRIPS:
          return {
            maxDuration: parseCustomDuration(report?.params['MDUR']),
            excludeLocalOperations: Boolean(report?.params['EL']),
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.NEAR_MISS_SHIP_TO_SHIP:
          const nearMissParams = safeJsonParse(
            report?.params['NMSSAS'],
            {} as Record<string, any>,
            'useInitializeMiscellaneous:NEAR_MISS_SHIP_TO_SHIP'
          );
          return {
            maxClosestPointOfApproachDistance: report?.params['MCD'],
            minDeltaCourseOverGround: nearMissParams['min_dcog'],
            maxDeltaCourseOverGround: nearMissParams['max_dcog'],
            minDeltaSpeedOverGround: nearMissParams['min_dsog'],
            maxDeltaSpeedOverGround: nearMissParams['max_dsog'],
            minSpeedOverGround: nearMissParams['min_sog'],
            maxSpeedOverGround: nearMissParams['max_sog'],
            minDuration: nearMissParams['min_dur'],
            maxDuration: nearMissParams['max_dur'],
            minTravelDistance: nearMissParams['min_dist'],
            maxTravelDistance: nearMissParams['max_dist'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.NEAR_MISS_SHIP_TO_POINT:
          return {
            distanceThreshold: report?.params['DT'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.PASSAGELINE:
          return {
            outputType: report?.params['PAC'],
            statisticGrouping: report?.params['STG']
              ? report.params['STG']
              : 'ungrouped',
            interval: report?.params['PI'],
            crossingConfig: report?.params['CC'] ? report.params['CC'] : false,
            minCrossingInterval: convertToMinCrossingInterval(
              report?.params['MCI'],
            ),
            minCrossingDistance: report?.params['MCPAD']
              ? report.params['MCPAD']
              : 0,
            columnDivisionValue: report?.params['DCDV'],
            columnDivisionUnit: report?.params['DCDU'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.PASSAGELINE_SHIPS:
          return {
            maxDuration: parseCustomDuration(report?.params['MDUR']),
            outputType: report?.params['PAC'],
            statisticGrouping: report?.params['STG'],
            interval: report?.params['PI'],
            bothDirections: report?.params['BDIR'] ? true : false,
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.TRANSPONDER_OFF:
          return {
            maxDuration: parseCustomDuration(report?.params['MDUR']),
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.SOG_DISTRIBUTION:
          return {
            distribution: report?.params['STG'],
            interval: report?.params['PI'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.FISHING:
          return {
            minDuration: parseCustomDuration(report?.params['MIDUR']),
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.MULTI_SHAPE:
          return {
            maxDuration: parseCustomDuration(report?.params['MDUR']),
            outputType: report?.params['PAC'],
            statisticGrouping: report?.params['STG'],
            interval: report?.params['PI'],
            bothDirections: report?.params['BDIR'] ? true : false,
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.PILOT_OPERATIONS:
          const pilotOpsParams = safeJsonParse(
            report?.params['SHPOP'],
            {} as Record<string, any>,
            'useInitializeMiscellaneous:PILOT_OPERATIONS'
          );
          return {
            maxClosestPointOfApproachDistance: pilotOpsParams['md'],
            minDeltaCourseOverGround: pilotOpsParams['min_dcog'],
            maxDeltaCourseOverGround: pilotOpsParams['max_dcog'],
            minDeltaSpeedOverGround: pilotOpsParams['min_dsog'],
            maxDeltaSpeedOverGround: pilotOpsParams['max_dsog'],
            minSpeedOverGround: pilotOpsParams['min_sog'],
            maxSpeedOverGround: pilotOpsParams['max_sog'],
            minDuration: pilotOpsParams['min_dur'],
            maxDuration: pilotOpsParams['max_dur'],
            minTravelDistance: pilotOpsParams['min_dist'],
            maxTravelDistance: pilotOpsParams['max_dist'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.TRANSIT_PILOTAGE:
          const transitPilotageParams = safeJsonParse(
            report?.params['SHPOP'],
            {} as Record<string, any>,
            'useInitializeMiscellaneous:TRANSIT_PILOTAGE'
          );
          return {
            maxClosestPointOfApproachDistance: transitPilotageParams['md'],
            minDeltaCourseOverGround: transitPilotageParams['min_dcog'],
            maxDeltaCourseOverGround: transitPilotageParams['max_dcog'],
            minDeltaSpeedOverGround: transitPilotageParams['min_dsog'],
            maxDeltaSpeedOverGround: transitPilotageParams['max_dsog'],
            minSpeedOverGround: transitPilotageParams['min_sog'],
            maxSpeedOverGround: transitPilotageParams['max_sog'],
            minDuration: transitPilotageParams['min_dur'],
            maxDuration: transitPilotageParams['max_dur'],
            minTravelDistance: transitPilotageParams['min_dist'],
            maxTravelDistance: transitPilotageParams['max_dist'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.COVERAGE:
          return {
            cellResolution: report?.params['CR'],
            colorDivision: report?.params['CD'],
            plotType: parseCoverageMiscellaneousPlotType(
              report?.params['CPTYP'],
            ),
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.CSV_DATA_EXPORT:
          return {
            dataFields: parseCsvMiscellaneousDataFields(report?.params['CSVP']),
            messageType: parseCsvMiscellaneousMessageTypes(
              report?.params['MSGT'],
            ),
            fewFiles: Boolean(report?.params['MF']),
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.TRACK:
          return {
            colorBy: report?.params['CB'],
            colorByInterval: report?.params['CBCD']
              ? convertBackToColorIntervals(
                  safeJsonParse(report.params['CBCD'], [], 'useInitializeMiscellaneous:TRACK')
                )
              : undefined,
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.WATCHDOG_EVENTS:
          return {
            watchdogDefinitions: matchingWatchdogAndEventDefinitions,
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.SQL:
          return {
            query: report?.params['SQL'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.ATON_DETAILED:
          return {
            mmsi: report?.params['MMSI'],
            ...notifyDefaults,
            ...shareDefaults,
          };
        case ReportBackendTypes.REPLAY_VIDEO: {
          // Parse video resolution from backend format {W, H} to frontend format "WxH"
          const vr = report?.params['VR'];
          const videoResolution = vr ? `${vr.W}x${vr.H}` : '640x480';

          // Parse video segments from backend format to frontend format
          const backendSegments = report?.params['VCE'] || [];
          const videoSegments = backendSegments.map((segment: any) => ({
            id: uuidv4(),
            dateRange: segment.start && segment.end
              ? { from: new Date(segment.start), to: new Date(segment.end) }
              : undefined,
            shapeId: segment.shapeUuid || undefined,
            shape: undefined, // Shape will be loaded separately if needed
            videoSpeed: segment.speed || 1,
            showLabels: segment.showLabels ?? true,
            showAllShips: segment.showAllShips ?? false,
          }));

          return {
            videoTitle: report?.params['VT'] || '',
            videoSpeed: report?.params['VS']?.toString() || '1',
            videoResolution,
            videoFormat: report?.params['VF'] || 'mp4',
            videoQuality: report?.params['VQ'] || 'low',
            mapType: report?.params['MAT'] || 'osm',
            videoSegments,
            showTracks: report?.params['ST'] ?? false,
            ...notifyDefaults,
            ...shareDefaults,
          };
        }
        default:
          return { ...notifyDefaults, ...shareDefaults };
      }
    },
    [
      isLoading,
      groups,
      report,
      reportType.type,
      matchingWatchdogAndEventDefinitions,
    ],
  );

  return { initializeMiscellaneous };
}
